import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import {
  RouterProvider,
  createBrowserHistory,
  createMemoryHistory,
  createRouter
} from '@tanstack/react-router'
import { type JSX, type ReactNode, useEffect, useState } from 'react'

import {
  initialSession,
  localViewerEmail,
  signInLocally,
  signInWithGoogle,
  signOutInternal,
  subscribeToSession,
  type InternalUser
} from './auth/firebase-auth.ts'
import { IdleGuard } from './components/idle-guard.tsx'
import { SignInScreen } from './components/sign-in-screen.tsx'
import { ThemeProvider } from './components/theme-provider.tsx'
import { TimeZoneProvider } from './components/time-zone-provider.tsx'
import { ThemeToggle } from './components/theme-toggle.tsx'
import { env, isAuthProvisioned } from './config/env.ts'
import { formatAuthError, type AuthAction } from './lib/auth-error.ts'
import { SessionProvider } from './session.tsx'
import { routeTree } from './routeTree.gen.ts'

/**
 * Build a router.
 *
 * A factory rather than a module singleton so a test gets an isolated history
 * instead of inheriting the previous test's location.
 *
 * @returns A router over the generated route tree.
 */
export function internalRouter() {
  const history =
    import.meta.env.MODE === 'test'
      ? createMemoryHistory({ initialEntries: ['/'] })
      : createBrowserHistory()

  return createRouter({ routeTree, history })
}

declare module '@tanstack/react-router' {
  interface Register {
    router: ReturnType<typeof internalRouter>
  }
}

function Shell({ children }: { children: ReactNode }): JSX.Element {
  return (
    <main
      data-testid="app-root"
      className="grid min-h-svh place-items-center p-6"
    >
      {/*
        The signed-out shell has no rail, and a reader who prefers dark meets
        this screen first. Corner-anchored so it never sits in the card's
        reading order.
      */}
      <div className="fixed top-4 right-4">
        <ThemeToggle data-testid="app-theme" />
      </div>
      <div className="bg-card grid w-full max-w-sm gap-3 rounded-lg border p-6 text-center shadow-sm">
        <h1
          data-testid="app-title"
          className="text-2xl font-semibold tracking-tight"
        >
          {'BlytzPay Internal'}
        </h1>
        <p data-testid="app-description" className="text-muted-foreground">
          {'The shared internal webapp'}
        </p>
        <p
          data-testid="app-env"
          className="text-muted-foreground font-mono text-xs"
        >
          {`${env.deployEnv} · ${env.appHostname} · tenant ${env.tenantId ?? 'unprovisioned'}`}
        </p>
        {children}
      </div>
    </main>
  )
}

export interface AppProps {
  /**
   * The router to mount. Defaults to a fresh one; tests pass their own so each
   * gets an isolated history.
   */
  router?: ReturnType<typeof internalRouter>
}

/**
 * AppSession — the signed-out screen, or the signed-in app.
 *
 * The session survives a reload and a new tab (ADR-0003): `subscribeToSession`
 * restores it from the one stored ID token before Firebase reports anything.
 * The refresh token is still memory-only, so a restored session lives until
 * that token's own `exp` and cannot renew itself.
 *
 * The router is mounted only on the signed-in side — sign-in is
 * `signInWithPopup`, which does not navigate the page, so an unauthenticated
 * deep link keeps its own URL and lands on the intended route once the session
 * appears. There is nothing to save and restore.
 *
 * @param props - Session props.
 * @param props.router - The router to mount.
 * @returns The signed-out shell, or the signed-in app.
 */
function AppSession({ router }: Required<AppProps>): JSX.Element {
  // Seeded, not left null: a restored session is known before the first
  // paint, and starting at null flashes the sign-in screen on every reload.
  const [user, setUser] = useState<InternalUser | null>(initialSession)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [queryClient] = useState(() => new QueryClient())

  useEffect(() => subscribeToSession(setUser), [])

  async function runAuth(
    run: () => Promise<void>,
    action: AuthAction
  ): Promise<void> {
    setBusy(true)
    setError(null)
    // Cleared on both paths rather than in a `finally`, which React Compiler
    // cannot analyze. The catch clears first, so a throw while formatting the
    // error still releases the button instead of wedging it.
    try {
      await run()
      setBusy(false)
    } catch (err) {
      setBusy(false)
      const message = formatAuthError(err, action)
      if (message) {
        setError(message)
      }
    }
  }

  if (!user) {
    // Which sign-in exists is a property of the build, not of this render, so
    // the address is read here rather than held in state.
    const localViewer = localViewerEmail()

    return (
      <Shell>
        <SignInScreen
          onSignIn={() => {
            void runAuth(
              localViewer === null ? signInWithGoogle : signInLocally,
              'sign-in'
            )
          }}
          busy={busy}
          error={error}
          provisioned={isAuthProvisioned(env)}
          localViewer={localViewer}
        />
      </Shell>
    )
  }

  return (
    <SessionProvider
      value={{
        userName: user.email ?? user.uid,
        error,
        // Returned, not fired and forgotten: IdleGuard waits for it so a
        // sign-out that fails leaves the guard armed.
        onSignOut: () => runAuth(signOutInternal, 'sign-out')
      }}
    >
      {/*
        Above the router on purpose. Rendered inside a route, an error while
        rendering that route would unmount the guard with the shell and leave
        an authenticated session with no idle logout. Idle protection is a
        property of the session, not of the layout.
      */}
      <IdleGuard onSignOut={() => runAuth(signOutInternal, 'sign-out')} />
      <QueryClientProvider client={queryClient}>
        <RouterProvider router={router} />
      </QueryClientProvider>
    </SessionProvider>
  )
}

/**
 * App — the application root.
 *
 * All this adds over `AppSession` is the theme, and it adds it HERE rather
 * than in `src/main.tsx` for two reasons. The provider has to sit above the
 * `user` branch, or signing in would unmount and remount it. And `App` is what
 * a test renders, so a test gets the same tree the browser does instead of a
 * subtree missing its context.
 *
 * @param props - App props.
 * @param props.router - The router. Injected so a test can supply one.
 * @returns The app.
 */
export function App({ router = internalRouter() }: AppProps = {}): JSX.Element {
  return (
    <ThemeProvider>
      <TimeZoneProvider>
        <AppSession router={router} />
      </TimeZoneProvider>
    </ThemeProvider>
  )
}
